import React, { useRef } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTrash, faPlus } from "@fortawesome/free-solid-svg-icons";
import { Choice } from "@/components/tasks/types";

interface ChoiceEditorProps {
  choices: Choice[];
  onChoicesChange: (choices: Choice[]) => void;
}

export default function ChoiceEditor({
  choices,
  onChoicesChange,
}: ChoiceEditorProps) {
  const inputRefs = useRef<HTMLInputElement[]>([]);

  const addChoice = () => {
    onChoicesChange([...choices, { name: "", value: "" }]);
  };

  const addChoiceAtIndex = (index: number) => {
    const newChoices = [...choices];
    newChoices.splice(index + 1, 0, { name: "", value: "" });
    onChoicesChange(newChoices);
  };

  const removeChoice = (index: number) => {
    const newChoices = choices.filter((_, i) => i !== index);
    onChoicesChange(newChoices);
  };

  const updateChoice = (index: number, field: keyof Choice, value: string) => {
    const newChoices = choices.map((choice, i) =>
      i === index ? { ...choice, [field]: value } : choice
    );
    onChoicesChange(newChoices);
  };

  const handlePaste = (
    index: number,
    e: React.ClipboardEvent<HTMLInputElement>
  ) => {
    const pastedText = e.clipboardData.getData("text");

    // Check if the pasted text contains newlines or tabs
    if (pastedText.includes("\n") || pastedText.includes("\t")) {
      e.preventDefault(); // Prevent default paste behavior

      let newChoices = [...choices];

      if (pastedText.includes("\n")) {
        // Split by newlines and create choices
        const lines = pastedText
          .split("\n")
          .filter((line) => line.trim() !== "");

        // Remove the current empty choice if it's empty and we're adding multiple
        if (
          newChoices[index].name === "" &&
          newChoices[index].value === "" &&
          lines.length > 0
        ) {
          newChoices.splice(index, 1);
        }

        // Create new choices from each line
        const choicesToAdd = lines.map((line) => {
          if (line.includes("\t")) {
            // If line contains tabs, split and use first as name, second as value
            const parts = line.split("\t");
            return {
              name: parts[0] || "",
              value: parts[1] || parts[0] || "",
            };
          } else {
            // Use the line as the name, and leave the value empty
            return { name: line, value: "" };
          }
        });

        // Insert new choices at the current index
        newChoices.splice(index, 0, ...choicesToAdd);
      } else if (pastedText.includes("\t")) {
        // Split by tabs and use first as name, second as value
        const parts = pastedText.split("\t");
        newChoices[index] = {
          name: parts[0] || "",
          value: parts[1] || parts[0] || "",
        };
      }

      onChoicesChange(newChoices);
    }
  };

  return (
    <>
      <label className="block text-sm font-medium text-gray-700">Choices</label>
      <p className="text-sm text-gray-400">
        If a choice's value is unspecified, the name will be used as the value.
        <br />
        You can paste a newline-delimited string to create multiple options at
        once.
        <br />
        Tab-delimited strings will use the first item as the name and the second
        as the value.
      </p>

      {choices.map((choice, index) => (
        <div key={index} className="flex space-x-2 items-center">
          <input
            type="text"
            placeholder="Display name"
            ref={(el) => {
              if (el) inputRefs.current[index] = el;
            }}
            value={choice.name}
            onChange={(e) => updateChoice(index, "name", e.target.value)}
            onPaste={(e) => handlePaste(index, e)}
            onKeyUp={(e) => {
              if (e.key === "Enter") {
                // If Enter is pressed, add a new choice below
                e.preventDefault();
                addChoiceAtIndex(index);

                setTimeout(() => {
                  inputRefs.current[index + 1]?.focus();
                }, 0);
              }
            }}
            className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
          <input
            type="text"
            placeholder="Value"
            value={choice.value}
            onChange={(e) => updateChoice(index, "value", e.target.value)}
            className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
          {choices.length > 1 && (
            <button
              type="button"
              onClick={() => removeChoice(index)}
              className="px-2 py-2 text-red-600 hover:text-red-800 focus:outline-none"
            >
              <FontAwesomeIcon icon={faTrash} />
            </button>
          )}
        </div>
      ))}
      <button
        type="button"
        onClick={addChoice}
        className="flex items-center space-x-2 px-3 py-2 text-blue-600 hover:text-blue-800 focus:outline-none"
      >
        <FontAwesomeIcon icon={faPlus} />
        <span>Add Choice</span>
      </button>
    </>
  );
}
